/**
 * Pure list-patch logic for SportsnewContext, extracted to a plain .js module
 * (no JSX/React imports) so it can be unit-tested directly with Node instead
 * of only via source-pattern regex matching.
 */

/* ── Canonical fixture ID — always a String so Set/Map keying is type-safe ── */
export const fxId = (f) => {
  const id = f?.fixtureId ?? f?.id;
  return id != null ? String(id) : null;
};

const ODD_OPEN_FLAGS = ['bettable', 'isBettable', 'is_bettable'];
const ODD_LOCK_FLAGS = ['locked', 'isLocked', 'is_locked', 'suspended', 'isSuspended', 'is_suspended', 'unavailable', 'betting_locked'];
const ODD_CONFLICT_FLAGS = [...ODD_LOCK_FLAGS, 'marketLocked'];
const ODD_STATUS_FIELDS = ['state', 'displayState', 'reason', 'lockReason'];
const ODD_LOCK_TEXT = /(^|[_\s-])(LOCK|LOCKED|SUSPEND|SUSPENDED|CLOSE|CLOSED|UNAVAILABLE|HIDDEN)([_\s-]|$)/i;
const hasOddLockConflict = shape => shape && (
  ODD_OPEN_FLAGS.some(key => shape[key] === false)
  || ODD_CONFLICT_FLAGS.some(key => shape[key] === true)
  || ODD_STATUS_FIELDS.some(key => ODD_LOCK_TEXT.test(String(shape[key] ?? '')))
);

// This is field normalization, not reopen authority. Event/request owners must
// first reject replay, cross-room ambiguity and stale Reader responses. A full
// accepted OPEN may replace inherited aliases; a price/status-only patch cannot.
export const normalizeAcceptedSportsnewOddAvailability = (existing, incoming) => {
  const defined = Object.fromEntries(Object.entries(incoming ?? {}).filter(([, value]) => value !== undefined));
  if (!existing || !/^(AVAILABLE|OPEN)$/i.test(String(defined.state ?? ''))
    || !ODD_OPEN_FLAGS.some(key => defined[key] === true)
    || hasOddLockConflict(defined)
    || hasOddLockConflict(defined.marketState ?? existing.marketState)) return defined;
  for (const key of ODD_OPEN_FLAGS) {
    if (defined[key] === undefined && existing[key] === false) defined[key] = true;
  }
  for (const key of ODD_LOCK_FLAGS) {
    if (defined[key] === undefined && existing[key] === true) defined[key] = false;
  }
  if (defined.displayState === undefined && ODD_LOCK_TEXT.test(String(existing.displayState ?? ''))) defined.displayState = 'active';
  for (const key of ['reason', 'lockReason', 'lockedSince', 'firstLockedAt']) {
    if (defined[key] === undefined && existing[key] != null) defined[key] = null;
  }
  return defined;
};

export const mergeOddPreservingIdentity = (existing, incoming) => {
  const defined = normalizeAcceptedSportsnewOddAvailability(existing, incoming);
  const merged = { ...existing, ...defined };
  if (existing?.oddStateId != null) merged.oddStateId = existing.oddStateId;
  if (existing?.id != null) merged.id = existing.id;
  const previousEvidence = sportsnewDisplayOddGroupEvidence(existing);
  const incomingEvidence = sportsnewDisplayOddGroupEvidence(incoming);
  const groups = { ...previousEvidence };
  if (previousEvidence || incomingEvidence) {
    for (const [group, fields] of Object.entries(SPORTSNEW_ODD_GROUP_FIELDS)) {
      if (fields.some(field => defined[field] !== undefined)) {
        groups[group] = incomingEvidence?.[group] ?? sportsnewOddEvidence(incoming);
      }
    }
  }
  const sameEvidence = Object.keys(groups).every(group => sameDisplayEvidence(groups[group], previousEvidence?.[group]));
  if (existing && sameEvidence && Object.keys(merged).every(key => merged[key] === existing[key])) return existing;
  return withSportsnewDisplayOddEvidence(merged, groups);
};

// Price and availability are independent partial-update groups. These helpers
// are pure. A non-enumerable display-only marker survives explicit display
// merges after bounded-ledger eviction, but never ordinary spread/JSON to cart
// or API. Never compare a source clock with an updated-at clock.
export const SPORTSNEW_ODD_GROUP_FIELDS = Object.freeze({
  price: ['priceDecimal', 'decimalOdds', 'odds', 'price'],
  availability: ['bettable', 'isBettable', 'is_bettable', 'locked', 'isLocked',
    'is_locked', 'suspended', 'isSuspended', 'is_suspended', 'unavailable',
    'state', 'displayState', 'reason', 'lockReason', 'lockedSince', 'firstLockedAt', 'betting_locked',
    'marketLocked', 'marketState'],
});
export const sportsnewOddEvidence = (patch = {}) => {
  const parse = value => {
    if (value == null || value === '') return null;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : null;
  };
  return {
    sourceReceivedAt: parse(patch.sourceReceivedAt ?? patch.source_received_at),
    updatedAt: parse(patch.updatedAt ?? patch.updated_at),
  };
};
const displayOddEvidence = Symbol('sportsnew.display.oddEvidence');
export const sportsnewDisplayOddGroupEvidence = odd => odd?.[displayOddEvidence];
const sameDisplayEvidence = (a, b) => a === b || (a && b
    && a.sourceReceivedAt === b.sourceReceivedAt && a.updatedAt === b.updatedAt
    && a.origin === b.origin
    && a.unverifiedRestHold === b.unverifiedRestHold
  && a.revision === b.revision && a.order?.room === b.order?.room
  && a.publicationRevision === b.publicationRevision
  && a.order?.connection === b.order?.connection && a.order?.epoch === b.order?.epoch && a.order?.sequence === b.order?.sequence);
export const withSportsnewDisplayOddEvidence = (odd, groups) => {
  if (!odd || !groups || !Object.keys(groups).length) return odd;
  const compact = {};
  for (const group of Object.keys(SPORTSNEW_ODD_GROUP_FIELDS)) {
    const evidence = groups[group];
    if (!evidence) continue;
    compact[group] = Object.freeze({ sourceReceivedAt: evidence.sourceReceivedAt,
      updatedAt: evidence.updatedAt, revision: evidence.revision, origin: evidence.origin,
      publicationRevision: evidence.publicationRevision,
      unverifiedRestHold: evidence.unverifiedRestHold,
      order: evidence.order ? Object.freeze({ room: evidence.order.room,
        connection: evidence.order.connection, epoch: evidence.order.epoch, sequence: evidence.order.sequence }) : null });
  }
  if (!Object.keys(compact).length) return odd;
  const current = sportsnewDisplayOddGroupEvidence(odd);
  if (current && Object.keys(compact).length === Object.keys(current).length
    && Object.keys(compact).every(group => sameDisplayEvidence(compact[group], current[group]))) return odd;
  const next = { ...odd };
  Object.defineProperty(next, displayOddEvidence, { value: Object.freeze(compact), enumerable: false });
  return next;
};
export const compareSportsnewOddEvidence = (incoming, existing) => {
  // Provider receive time and transaction-start time are not a DB state
  // revision. Only a sequence in the same connection/room is comparable.
  const left = incoming?.order, right = existing?.order;
  if (left && right && left.room === right.room && left.connection === right.connection && left.epoch === right.epoch
    && Number.isSafeInteger(left.sequence) && Number.isSafeInteger(right.sequence))
    return left.sequence - right.sequence;
  return 0;
};

export const mapSportsnewMarketOdds = (markets, mapper) => {
  if (!Array.isArray(markets)) return markets;
  let changed = false;
  const next = markets.map(market => {
    let result = market;
    for (const key of ['selections', 'odds']) {
      if (!Array.isArray(market?.[key])) continue;
      const rows = market[key].map(odd => odd && typeof odd === 'object' ? mapper(odd) : odd);
      if (rows.some((odd, index) => odd !== market[key][index])) {
        result = { ...result, [key]: rows };
      }
    }
    if (result !== market) changed = true;
    return result;
  });
  return changed ? next : markets;
};

export const mapSportsnewFixtureOdds = (fixture, mapper) => {
  if (!fixture || typeof fixture !== 'object') return fixture;
  let next = fixture;
  if (fixture.mainOdds) {
    const mainOdds = { ...fixture.mainOdds };
    let changed = false;
    for (const slot of ['home', 'draw', 'away']) {
      const old = fixture.mainOdds[slot];
      if (!old) continue;
      mainOdds[slot] = mapper(old);
      if (mainOdds[slot] !== old) changed = true;
    }
    if (changed) next = { ...next, mainOdds };
  }
  for (const key of ['marketsPreview', 'markets', 'marketSnapshot']) {
    const markets = mapSportsnewMarketOdds(fixture[key], mapper);
    if (markets !== fixture[key]) next = { ...next, [key]: markets };
  }
  if (Array.isArray(fixture.odds)) {
    const odds = fixture.odds.map(mapper);
    if (odds.some((odd, index) => odd !== fixture.odds[index])) next = { ...next, odds };
  }
  return next;
};

// Match applyListPatch precedence exactly: fixtures, then ops, then fields.
export const sportsnewListPatchFixtures = (patch = {}) => {
  const fixtures = fixtureArrayFromPatchFixtures(patch.fixtures);
  if (fixtures.length) {
    const seen = new Set();
    return fixtures.filter(fixture => {
      const id = fxId(fixture);
      if (id == null || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }
  if (Array.isArray(patch.ops)) return patch.ops
    .filter(op => (op?.op ?? op?.type) === 'upsert' && op.fixture)
    .map(op => op.fixture);
  if (patch.fixtureId != null && patch.fields) {
    const fields = {};
    const names = Array.isArray(patch.fields) ? patch.fields : Object.keys(patch.fields);
    names.forEach(name => {
      const value = patch[name] !== undefined ? patch[name]
        : !Array.isArray(patch.fields) ? patch.fields[name] : undefined;
      if (value !== undefined) fields[name] = value;
    });
    return [{ ...fields, fixtureId: patch.fixtureId }];
  }
  return [];
};

const mapListPatchFixtures = (patch, mapper) => {
  if (!patch) return patch;
  if (fixtureArrayFromPatchFixtures(patch.fixtures).length) {
    return { ...patch, fixtures: sportsnewListPatchFixtures(patch).map(mapper) };
  }
  if (Array.isArray(patch.ops)) return { ...patch, ops: patch.ops.map(op => (
    (op?.op ?? op?.type) === 'upsert' && op.fixture
      ? { ...op, fixture: mapper(op.fixture) }
      : op
  )) };
  const fixture = sportsnewListPatchFixtures(patch)[0];
  if (!fixture) return patch;
  const mapped = mapper(fixture);
  const next = { ...patch };
  const names = Array.isArray(patch.fields) ? patch.fields : Object.keys(patch.fields ?? {});
  names.forEach(name => { delete next[name]; });
  return { ...next, ...mapped, fields: Array.isArray(patch.fields)
    ? names.filter(name => mapped[name] !== undefined) : mapped };
};
export const mapSportsnewListPatchOdds = (patch, mapper) => mapListPatchFixtures(patch,
  fixture => mapSportsnewFixtureOdds(fixture, odd => mapper(odd, fxId(fixture))));

// Only the existing, in-flight recovery owner holds this bounded journal.
// It is not a second global stream/history and is discarded with that request.
const recoveryOddFields = new Set(['fixtureId', 'id', 'mainOdds', 'marketsPreview', 'markets', 'marketSnapshot', 'odds']);
export const trackSportsnewListRecoveryFields = (fence, patch, sequence, controlIds = []) => {
  if (!fence || fence.invalid || !Number.isSafeInteger(sequence)) return;
  const record = (id, field) => {
    if (id == null) return;
    id = String(id);
    let fields = fence.byFixture.get(id);
    if (!fields) {
      if (fence.byFixture.size >= 1000) { fence.invalid = true; return; }
      fields = new Map(); fence.byFixture.set(id, fields);
    }
    if (!fields.has(field) && fields.size >= 128) { fence.invalid = true; return; }
    fields.set(field, Math.max(fields.get(field) ?? 0, sequence));
  };
  sportsnewListPatchFixtures(patch).forEach(fixture => Object.keys(fixture).forEach(field => {
    if (!recoveryOddFields.has(field) && fixture[field] !== undefined) record(fxId(fixture), field);
  }));
  controlIds.forEach(id => record(id, '$membership'));
};
export const filterSportsnewListRecoveryFields = (patch, fence, sequence) => {
  if (!fence || !Number.isSafeInteger(sequence)) return patch;
  return mapListPatchFixtures(patch, fixture => {
    const fields = fence.byFixture.get(fxId(fixture));
    if (!fields) return fixture;
    const next = { ...fixture };
    for (const [field, latest] of fields) {
      if (field !== '$membership' && sequence <= latest) delete next[field];
    }
    return next;
  });
};
export const reconcileSportsnewListRecoveryRows = (rows, fence, sequence, currentRows) => {
  if (!fence) return rows;
  const currentById = new Map(currentRows.map(row => [fxId(row), row]));
  return rows.map(row => {
    const current = currentById.get(fxId(row));
    const fields = fence.byFixture.get(fxId(row));
    if (!current || !fields) return row;
    const next = { ...row };
    for (const [field, latest] of fields) {
      if (field !== '$membership' && sequence <= latest && current[field] !== undefined) next[field] = current[field];
    }
    return next;
  });
};

export const applySportsnewOddPatchesToMarkets = (markets, patches = []) => {
  const byId = new Map();
  for (const patch of patches) {
    const id = patch?.oddStateId ?? patch?.id;
    if (id != null) byId.set(String(id), mergeOddPreservingIdentity(byId.get(String(id)), patch));
  }
  if (!byId.size) return markets;
  return mapSportsnewMarketOdds(markets, odd => {
    const patch = byId.get(oddItemId(odd));
    return patch ? mergeOddPreservingIdentity(odd, normalizeOddPatchForList(patch)) : odd;
  });
};

// Decode the actual API roomDeltas envelope. A latestSequence value alone is
// not proof of recovery; deltas must cover every sequence after the request.
export const normalizeSportsnewListRecovery = (response, { room, since } = {}) => {
  if (response?.status === false || response?.networkError || response?.error
    || (response?.httpStatus != null && (response.httpStatus < 200 || response.httpStatus >= 300))) return null;
  const data = response?.data;
  if (!data || (data.room != null && data.room !== room)) return null;
  const sequence = data.latestSequence ?? data.lastSequence ?? data.sequence;
  if (sequence != null && (!Number.isSafeInteger(sequence) || sequence < 0)) return null;
  if (data.gap === true) {
    const snapshot = data.snapshot;
    if (!snapshot || !Array.isArray(snapshot.rows) || !snapshot.subscription
      || !Number.isSafeInteger(sequence) || sequence < since
      || !Number.isInteger(snapshot.page) || snapshot.page < 1
      || !Number.isInteger(snapshot.limit) || snapshot.limit < 1
      || !Number.isSafeInteger(snapshot.total) || snapshot.total < 0
      || snapshot.rows.length > snapshot.limit || snapshot.rows.length > snapshot.total) return null;
    return { kind: 'snapshot', snapshot, sequence };
  }
  if (Array.isArray(data.deltas)) {
    const bySequence = new Map();
    for (const delta of data.deltas) {
      if (!Number.isSafeInteger(delta?.sequence) || !delta.patch || typeof delta.patch !== 'object'
        || (delta.event && delta.event !== 'sportsnew:list:patch')
        || (delta.patch.room != null && delta.patch.room !== room)) return null;
      if (delta.sequence > since && !bySequence.has(delta.sequence)) bySequence.set(delta.sequence, delta);
    }
    const deltas = [...bySequence.values()].sort((a, b) => a.sequence - b.sequence);
    let covered = since;
    for (const delta of deltas) {
      if (delta.sequence !== covered + 1) return null;
      covered = delta.sequence;
    }
    if (sequence != null && covered !== sequence) return null;
    return { kind: 'deltas', deltas, sequence: covered };
  }
  const rows = Array.isArray(data) ? data : data.rows ?? data.fixtures;
  if (!Array.isArray(rows)) return null;
  return { kind: 'legacy', rows, subscription: data.subscription,
    sequence: rows.length ? sequence : null };
};

export const oddStateIdKey = (value) => {
  const id = value?.oddStateId ?? value?.id ?? value;
  return id != null ? String(id) : null;
};

export const oddPatchId = (patch) => {
  const id = patch?.oddStateId ?? patch?.id;
  return oddStateIdKey(id);
};

export const oddItemId = (odd) => {
  const id = odd?.oddStateId ?? odd?.id;
  return oddStateIdKey(id);
};

export const normalizeOddPatchForList = (patch) => {
  const nextPrice = patch?.priceDecimal ?? patch?.decimalOdds ?? patch?.odds ?? patch?.price;
  const normalized = {};
  [
    'oddStateId',
    'id',
    'marketStateId',
    'priceDecimal',
    'odds',
    'price',
    'bettable',
    'isBettable',
    'locked',
    'suspended',
    'is_suspended',
    'state',
    'displayState',
    'reason',
    'lockReason',
    'marketLocked', 'marketState',
    'decimalOdds', 'is_bettable', 'isLocked', 'is_locked', 'isSuspended', 'unavailable',
    'lockedSince', 'firstLockedAt', 'betting_locked', 'sourceReceivedAt', 'source_received_at', 'updatedAt', 'updated_at',
  ].forEach((key) => {
    if (patch?.[key] !== undefined) normalized[key] = patch[key];
  });
  if (nextPrice != null) normalized.priceDecimal = nextPrice;
  return withSportsnewDisplayOddEvidence(normalized, sportsnewDisplayOddGroupEvidence(patch));
};

export const fixtureArrayFromPatchFixtures = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    return Object.values(value).filter((item) => item && typeof item === 'object');
  }
  return [];
};
